import React, { FC } from "react";
import { Box, Text } from "@mantine/core";

import { getArchFromTeeth } from "../../utils/product.utils";
import { Product, RemovableItem } from "../../types/interfaces";
import {
  ItemType,
  WaxUpType,
  WaxUpTypeEn,
  WaxUpTypeFr,
  ItemMaterials,
} from "../../types/enums";
import { getItemShadeLabel, isAllMissingTeeth } from "../../utils/item.utils";
import {
  getDentureMaterialLabel,
  getDentureShadeLabel,
  getDentureShadeTypeLabel,
  getGingivaShadeLabel,
  getPartialDentureArchPreferences,
  getPartialDentureOptionLabels,
  getTeethShapeLabel,
  getTeethToRemove,
} from "../../utils/denture.utils";
import { getItemMaterialLabel } from "../../utils/item.utils";
import { translateItemMaterial } from "../../utils/translation.utils";

type ItemsDescriptionProps = {
  item: RemovableItem;
  product?: Product;
  shouldTranslate: boolean;
  choicesData?: any;
};

export const ItemsDescriptionRemovableItem: FC<ItemsDescriptionProps> = ({
  item,
  product,
  shouldTranslate,
  choicesData,
}) => {
  if (!item) return null;

  const deduceWaxUpType = (
    type: WaxUpType,
    shouldTranslate: boolean = false
  ) => {
    if (shouldTranslate) {
      return WaxUpTypeEn[type];
    }
    return WaxUpTypeFr[type];
  };

  const teethToRemove = getTeethToRemove(item);
  const optionLabels = getPartialDentureOptionLabels(item, shouldTranslate);
  const showOptions = optionLabels.length > 0;
  const partialDentureArchPreferences = getPartialDentureArchPreferences(
    item,
    product
  );

  const getShadeTypeLabel = (shadeType: string) =>
    getDentureShadeTypeLabel(shadeType, shouldTranslate);

  const getTeethShadeLabel = (teethShape: string) =>
    getTeethShapeLabel(teethShape, shouldTranslate);

  const getMaterialLabel = (materialId: string): string =>
    getDentureMaterialLabel(materialId, shouldTranslate);

  const getShadeLabel = (shadeId: string): string =>
    getDentureShadeLabel(shadeId, shouldTranslate, choicesData);

  const getGingivaLabel = (gingivaId: string): string =>
    getGingivaShadeLabel(gingivaId, shouldTranslate);

  return (
    <>
      {item.itemType === ItemType.WAX_UP && (
        <Text size="sm">
          {`- Type : ${deduceWaxUpType(item.waxUpType, shouldTranslate)}`}
        </Text>
      )}
      {item.teethShade &&
        item.itemType !== ItemType.FULL_DENTURE &&
        !item.partialDentureOrderType && (
          <Text size="sm">
            {shouldTranslate
              ? `- Item shade : ${getItemShadeLabel(item)}`
              : `- Teinte : ${getItemShadeLabel(item)}`}
          </Text>
        )}
      {(item.itemType === ItemType.PARTIAL_DENTURE ||
        item.itemType === ItemType.FULL_DENTURE ||
        item.itemType === ItemType.FULL_DENTURE_WAX_BITE ||
        item.itemType === ItemType.SPLINT ||
        item.itemType === ItemType.BLEACHING_TRAY ||
        item.itemType === ItemType.RETAINER ||
        item.itemType === ItemType.NIGHT_GUARD ||
        item.itemType === ItemType.RETENTION_WIRE ||
        item.itemType === ItemType.CUSTOM_TRAY ||
        item.itemType === ItemType.IMMEDIATE_DENTURE ||
        item.itemType === ItemType.WAX_BITE) && (
        <Text size="sm">
          {item.itemType === ItemType.FULL_DENTURE ||
          item.itemType === ItemType.FULL_DENTURE_WAX_BITE ||
          (item.itemType === ItemType.PARTIAL_DENTURE &&
            item.partialDentureOrderType)
            ? `- ${shouldTranslate ? "" : "Arcade"} ${getArchFromTeeth(item.teeth, shouldTranslate)}`
            : `- ${getArchFromTeeth(item.teeth, shouldTranslate)}`}
        </Text>
      )}
      {item.itemType === ItemType.PARTIAL_DENTURE &&
        item.partialDentureOrderType && (
          <>
            {item?.itemMaterial && (
              <Text size="sm">
                {shouldTranslate
                  ? `- Material : ${getMaterialLabel(item.itemMaterial.id)}`
                  : `- Matériau : ${getMaterialLabel(item.itemMaterial.id)}`}
              </Text>
            )}
            {item.teethShade && (
              <Text size="sm">
                {shouldTranslate
                  ? `- Teeth shade : ${getShadeLabel(item.teethShade.id)}`
                  : `- Teinte : ${getShadeLabel(item.teethShade.id)}`}
              </Text>
            )}
            {(() => {
              const arcadeShadeType = partialDentureArchPreferences.shadeType;
              return arcadeShadeType ? (
                <Text size="sm">
                  {shouldTranslate
                    ? `- Shade type : ${getShadeTypeLabel(arcadeShadeType)}`
                    : `- Type de teinte : ${getShadeTypeLabel(arcadeShadeType)}`}
                </Text>
              ) : null;
            })()}
            {item.gingivaShade && (
              <Text size="sm">
                {shouldTranslate
                  ? `- Gingiva shade : ${getGingivaLabel(item.gingivaShade.id)}`
                  : `- Teinte de la gencive : ${getGingivaLabel(item.gingivaShade.id)}`}
              </Text>
            )}
            {(() => {
              const arcadeTeethShape = partialDentureArchPreferences.teethShape;
              return arcadeTeethShape ? (
                <Text size="sm">
                  {shouldTranslate
                    ? `- Teeth shape : ${getTeethShadeLabel(arcadeTeethShape)}`
                    : `- Forme des dents : ${getTeethShadeLabel(arcadeTeethShape)}`}
                </Text>
              ) : null;
            })()}
          </>
        )}
      {item.itemType === ItemType.FULL_DENTURE && (
        <>
          {product?.itemPreferences?.fullDentureArcadeData ? (
            // per arcade mode
            <>
              <Text size="sm" fw={600}>
                {shouldTranslate ? "- Upper Arch" : "- Arcade Maxillaire"}
              </Text>
              <Box ml="md">
                {product.itemPreferences.fullDentureArcadeData.maxillaire
                  .itemMaterialId && (
                  <Text size="sm">
                    {shouldTranslate ? "Material: " : "Matériau : "}
                    {getMaterialLabel(
                      product.itemPreferences.fullDentureArcadeData.maxillaire
                        .itemMaterialId
                    )}
                  </Text>
                )}
                {product.itemPreferences.fullDentureArcadeData.maxillaire
                  .shadeType && (
                  <Text size="sm">
                    {shouldTranslate ? "Shade type: " : "Type de teinte : "}
                    {getShadeTypeLabel(
                      product.itemPreferences.fullDentureArcadeData.maxillaire
                        .shadeType
                    )}
                  </Text>
                )}
                {product.itemPreferences.fullDentureArcadeData.maxillaire
                  .teethShadeId && (
                  <Text size="sm">
                    {shouldTranslate ? "Teeth shade: " : "Teinte des dents : "}
                    {getShadeLabel(
                      product.itemPreferences.fullDentureArcadeData.maxillaire
                        .teethShadeId
                    )}
                  </Text>
                )}
                {product.itemPreferences.fullDentureArcadeData.maxillaire
                  .gingivaShadeId && (
                  <Text size="sm">
                    {shouldTranslate
                      ? "Gingiva shade: "
                      : "Teinte de gencive : "}
                    {getGingivaLabel(
                      product.itemPreferences.fullDentureArcadeData.maxillaire
                        .gingivaShadeId
                    )}
                  </Text>
                )}
                {product.itemPreferences.fullDentureArcadeData.maxillaire
                  .teethShape && (
                  <Text size="sm">
                    {shouldTranslate ? "Teeth shape: " : "Forme des dents : "}
                    {getTeethShadeLabel(
                      product.itemPreferences.fullDentureArcadeData.maxillaire
                        .teethShape
                    )}
                  </Text>
                )}
              </Box>

              <Text size="sm" fw={600} mt="xs">
                {shouldTranslate ? "- Lower Arch" : "- Arcade Mandibulaire"}
              </Text>
              <Box ml="md">
                {product.itemPreferences.fullDentureArcadeData.mandibulaire
                  .itemMaterialId && (
                  <Text size="sm">
                    {shouldTranslate ? "Material: " : "Matériau : "}
                    {getMaterialLabel(
                      product.itemPreferences.fullDentureArcadeData.mandibulaire
                        .itemMaterialId
                    )}
                  </Text>
                )}
                {product.itemPreferences.fullDentureArcadeData.mandibulaire
                  .shadeType && (
                  <Text size="sm">
                    {shouldTranslate ? "Shade type: " : "Type de teinte : "}
                    {getShadeTypeLabel(
                      product.itemPreferences.fullDentureArcadeData.mandibulaire
                        .shadeType
                    )}
                  </Text>
                )}
                {product.itemPreferences.fullDentureArcadeData.mandibulaire
                  .teethShadeId && (
                  <Text size="sm">
                    {shouldTranslate ? "Teeth shade: " : "Teinte des dents : "}
                    {getShadeLabel(
                      product.itemPreferences.fullDentureArcadeData.mandibulaire
                        .teethShadeId
                    )}
                  </Text>
                )}
                {product.itemPreferences.fullDentureArcadeData.mandibulaire
                  .gingivaShadeId && (
                  <Text size="sm">
                    {shouldTranslate
                      ? "Gingiva shade: "
                      : "Teinte de gencive : "}
                    {getGingivaLabel(
                      product.itemPreferences.fullDentureArcadeData.mandibulaire
                        .gingivaShadeId
                    )}
                  </Text>
                )}
                {product.itemPreferences.fullDentureArcadeData.mandibulaire
                  .teethShape && (
                  <Text size="sm">
                    {shouldTranslate ? "Teeth shape: " : "Forme des dents : "}
                    {getTeethShadeLabel(
                      product.itemPreferences.fullDentureArcadeData.mandibulaire
                        .teethShape
                    )}
                  </Text>
                )}
              </Box>
            </>
          ) : (
            // single mode
            <>
              {item?.itemMaterial && (
                <Text size="sm">
                  {shouldTranslate
                    ? `- Item material : ${translateItemMaterial(item?.itemMaterial.id as ItemMaterials)}`
                    : `- Matériau : ${getItemMaterialLabel(item)}`}
                </Text>
              )}
              {item.teethShade && (
                <Text size="sm">
                  {shouldTranslate
                    ? `- Item shade : ${getItemShadeLabel(item)}`
                    : `- Teinte : ${getItemShadeLabel(item)}`}
                </Text>
              )}
              {product?.itemPreferences?.shadeType && (
                <Text size="sm">
                  {shouldTranslate
                    ? `- Shade type : ${getShadeTypeLabel(product.itemPreferences.shadeType)}`
                    : `- Type de teinte : ${getShadeTypeLabel(product.itemPreferences.shadeType)}`}
                </Text>
              )}
              {item.gingivaShade && (
                <Text size="sm">
                  {shouldTranslate
                    ? `- Gingiva shade: ${getGingivaLabel(item.gingivaShade.id)}`
                    : `- Teinte de gencive : ${getGingivaLabel(item.gingivaShade.id)}`}
                </Text>
              )}
              {product?.itemPreferences?.teethShape && (
                <Text size="sm">
                  {shouldTranslate
                    ? `- Teeth shape : ${getTeethShadeLabel(product.itemPreferences.teethShape)}`
                    : `- Forme des dents : ${getTeethShadeLabel(product.itemPreferences.teethShape)}`}
                </Text>
              )}
            </>
          )}
        </>
      )}
      {item.itemType === ItemType.PARTIAL_DENTURE && (
        <Text size="sm">
          {(() => {
            if (isAllMissingTeeth(item)) {
              return shouldTranslate
                ? "- To include : all missing teeth"
                : "- Dents à inclure : toutes les dents manquantes";
            }
            return shouldTranslate
              ? `- To include : ${item.teeth.join(", ")}`
              : `- Dents à inclure : ${item.teeth.join(", ")}`;
          })()}
        </Text>
      )}
      {item?.partialDenture && (
        <>
          {item.partialDenture.missingTeeth && (
            <Text size="sm" style={{ marginLeft: "1rem", textAlign: "left" }}>
              {(() => {
                if (isAllMissingTeeth(item)) {
                  return shouldTranslate
                    ? "- Missing teeth : all missing teeth"
                    : "- Dents manquantes : toutes les dents manquantes";
                }
                return shouldTranslate
                  ? `- Missing teeth : ${item.partialDenture.missingTeeth.join(", ")}`
                  : `- Dents manquantes : ${item.partialDenture.missingTeeth.join(", ")}`;
              })()}
            </Text>
          )}
          {item.partialDenture.missingTeeth && (
            <Text size="sm" style={{ marginLeft: "1rem", textAlign: "left" }}>
              {shouldTranslate
                ? `- Teeth to remove : ${teethToRemove.join(", ")}`
                : `- Dents à retirer : ${teethToRemove.join(", ")}`}
            </Text>
          )}
          {item.gingivaShade && !item.partialDentureOrderType && (
            <Text size="sm">
              {shouldTranslate
                ? `- Gingiva shade : ${item.gingivaShade.id}`
                : `- Teinte de la gencive : ${item.gingivaShade.label}`}
            </Text>
          )}
          {item.isReplacement && (
            <Text size="sm">
              {shouldTranslate ? `- Replacement` : `- Remplacement`}
            </Text>
          )}
          {item.partialDenture.hooks &&
            item.partialDenture.hooks.length > 0 && (
              <Box>
                <Text size="sm">
                  {shouldTranslate ? `- Clasp` : `- Crochets`}:
                </Text>
                <div style={{ marginLeft: "1rem", textAlign: "left" }}>
                  {item.partialDenture.hooks.map(hook => (
                    <Text size="sm" key={hook.id}>
                      {shouldTranslate
                        ? `- ${hook.hooksMaterial.id} ${shouldTranslate ? `clasp` : ``} ${hook.hooksPosition.length > 0 ? `(${hook.hooksPosition.join(", ")})` : ""}`
                        : `- ${hook.hooksMaterial.label} ${shouldTranslate ? `clasp` : ``} ${hook.hooksPosition.length > 0 ? `(${hook.hooksPosition.join(", ")})` : ""}`}
                    </Text>
                  ))}
                </div>
              </Box>
            )}
          {showOptions && (
            <Box size="sm">
              <Text size="sm">- Options:</Text>
              <div style={{ marginLeft: "1rem", textAlign: "left" }}>
                {optionLabels.map(optionLabel => (
                  <Text size="sm" key={optionLabel}>
                    {`- ${optionLabel}`}
                  </Text>
                ))}
              </div>
            </Box>
          )}
        </>
      )}
    </>
  );
};
